/**
 * Idle-state right panel: a live preview card that mirrors the form input.
 *
 * Self-contained: takes form values via props (no Formik context coupling).
 */

import { Sparkles, Globe, Hash } from "lucide-react"
import { PRICING_ENGINE_LABELS } from "../../../types/pricing-engine"
import { EnrollFormValues } from "./enroll-form"

interface Props {
  values: EnrollFormValues
}

const HEX_RX = /^#[0-9a-fA-F]{6}$/

const initials = (name: string): string => {
  const trimmed = name.trim()
  if (!trimmed) return "?"
  const parts = trimmed.split(/\s+/).slice(0, 2)
  return parts.map(p => p[0]?.toUpperCase() ?? "").join("")
}

const BrandPreview = ({ values }: Props) => {
  const primary = HEX_RX.test(values.primary_color)
    ? values.primary_color
    : "#3d1080"
  const secondary = HEX_RX.test(values.secondary_color)
    ? values.secondary_color
    : "#013a70"
  const hasLogo = !!values.logo_url
  const engineLabel =
    values.api_mode && PRICING_ENGINE_LABELS[values.api_mode]
      ? PRICING_ENGINE_LABELS[values.api_mode]
      : null
  const orgName = values.org_name?.trim()

  return (
    <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
      {/* Header swatch */}
      <div
        className="relative h-32 px-6 py-5"
        style={{
          background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`
        }}
      >
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.5),transparent_60%)]" />
        <div className="relative flex items-center gap-2 text-white/80 text-xs font-mono uppercase tracking-widest">
          <Sparkles size={14} />
          Live preview
        </div>
        <div className="relative mt-3 text-white text-xs font-mono opacity-80">
          {values.website || "https://fivestar.mortgage"}
        </div>
      </div>

      {/* Logo / initials */}
      <div className="-mt-10 px-6 relative">
        <div className="h-20 w-20 rounded-2xl bg-white shadow-md ring-1 ring-neutral-100 flex items-center justify-center overflow-hidden">
          {hasLogo ? (
            <img
              src={values.logo_url}
              alt="Company logo"
              className="h-full w-full object-contain"
              onError={e => {
                ;(e.currentTarget as HTMLImageElement).style.display = "none"
              }}
            />
          ) : (
            <span className="font-serif text-2xl" style={{ color: primary }}>
              {initials(values.company_name)}
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-5 space-y-4">
        <div>
          <h3 className="font-serif text-xl text-neutral-900 leading-tight">
            {values.company_name || "FiveStar Mortgage"}
          </h3>
          {orgName && orgName !== (values.company_name || "") && (
            <p className="mt-0.5 text-xs text-neutral-500">Org: {orgName}</p>
          )}
          <p className="mt-1 flex items-center gap-1.5 text-xs text-neutral-500">
            <Hash size={12} />
            NMLS {values.company_nmls || "—"}
          </p>
        </div>

        <div className="flex items-center gap-2 text-sm text-neutral-700">
          <Globe size={14} className="text-neutral-400" />
          <span className="truncate">
            {values.website || "Website goes here"}
          </span>
        </div>

        {/* Color swatches */}
        <div className="flex items-center gap-3 pt-2 border-t border-neutral-100">
          <span className="text-xs font-mono uppercase tracking-wider text-neutral-400">
            Palette
          </span>
          <div className="flex items-center gap-2">
            <div
              className="h-5 w-5 rounded border border-neutral-200"
              style={{ backgroundColor: primary }}
              title={`Primary ${primary}`}
            />
            <div
              className="h-5 w-5 rounded border border-neutral-200"
              style={{ backgroundColor: secondary }}
              title={`Secondary ${secondary}`}
            />
          </div>
        </div>

        {/* Engine */}
        {engineLabel && (
          <div className="flex items-center justify-between pt-2 border-t border-neutral-100">
            <span className="text-xs font-mono uppercase tracking-wider text-neutral-400">
              Engine
            </span>
            <span className="text-sm text-neutral-700">{engineLabel}</span>
          </div>
        )}

        {/* Admin */}
        <div className="rounded-lg bg-neutral-50 px-4 py-3 border border-neutral-100">
          <div className="text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1">
            First admin
          </div>
          <div className="text-sm text-neutral-900">
            {(
              values.first_user.first_name +
              " " +
              values.first_user.last_name
            ).trim() || "—"}
          </div>
          <div className="text-xs text-neutral-500 truncate">
            {values.first_user.email || "—"}
          </div>
        </div>
      </div>
    </div>
  )
}

export default BrandPreview
